import React, { useState, useEffect, createContext, useCallback } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';

import { useWindowSize, sizes } from 'helpers/MediaHelper';

import { showFailure } from 'actions/notificationActions';

import { Tabs, Row, Col, Typography, Modal } from 'antd';

import {
  getPolicies,
  getAssociations,
  createPolicy,
  updatePolicy,
  deletePolicy,
} from './store/actions';

import { Draft, Published, Archived, Statuses } from './components/Form/constants';

import PoliciesTable from './components/PoliciesTable';
import RowActions from './components/Table/RowActions';
import PolicyForm from './components/PolicyForm';
import ActionButtons from './components/Form/ActionButtons';
import { getTranslatedString } from 'helpers/i18n';

export const PoliciesContext = createContext({});

const CREATE_FORM = 'create_policy_form';
const EDIT_FORM = 'edit_policy_form';

const modes = {
  LIST: 'list',
  CREATE: 'create',
  EDIT: 'edit',
};

const GreyContainer = styled.div`
  background-color: #fafafa;
`;

const LandlordPolicies = ({ showFailure }) => {
  const [policies, setPolicies] = useState([]);
  const [policyAssociations, setPolicyAssociations] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState(modes.LIST);
  const [currentPolicy, setCurrentPolicy] = useState({});

  const [width] = useWindowSize();
  const isMobile = width <= sizes.mobile;

  const loadAssociations = useCallback(() => {
    getAssociations()
      .done((response) => {
        setPolicyAssociations(response.sites);
      })
      .catch((error) => {
        showFailure(error);
      });
  }, [showFailure]);

  const loadPolicies = useCallback(() => {
    setLoading(true);
    getPolicies()
      .done((response) => {
        setPolicies(response.policies);
        setLoading(false);
      })
      .catch((error) => {
        showFailure(error);
      });
  }, [showFailure]);

  // Load the data needed to populate the context,
  // so that common data can be shared with descendant components
  // through the `useContext` hook.
  useEffect(() => {
    loadAssociations();
    loadPolicies();
  }, [loadPolicies, loadAssociations]);

  const setCreateModePolicy = () => {
    setCurrentPolicy({});
    setMode(modes.CREATE);
  };

  const getPolicy = (policyId) => {
    return policies.find((p) => p.id === policyId);
  };

  const editPolicy = (policyId) => {
    setCurrentPolicy(getPolicy(policyId));
    setMode(modes.EDIT);
  };

  const duplicatePolicy = (policyId) => {
    const existing = getPolicy(policyId);
    const duplicate = Object.assign({}, existing);
    delete duplicate.id;
    duplicate.rules.forEach((e) => {
      ['created_at', 'updated_at', 'id'].forEach((k) => delete e[k]);
    });
    duplicate.title = `Copy of ${duplicate.title}`;

    setCurrentPolicy(duplicate);
    setMode(modes.CREATE);
  };

  const handleCreate = async (values) => {
    try {
      setLoading(true);
      await createPolicy(values);

      Modal.success({
        title: getTranslatedString('landlordPortal.policies.successDraftTitle'),
        content: getTranslatedString('landlordPortal.policies.successDraftDesc', {
          policy_title: values.title,
        }),
        onOk: setMode(modes.LIST),
      });

      loadPolicies();
    } catch (error) {
      showFailure(error);
    }
  };

  const handleUpdate = async (values, policyId) => {
    try {
      setLoading(true);
      await updatePolicy(values, policyId);

      Modal.success({
        title: getTranslatedString('landlordPortal.policies.successSavedTitle'),
        content: getTranslatedString('landlordPortal.policies.successSavedDesc', {
          policy_title: values.title,
        }),
        onOk: setMode(modes.LIST),
      });

      loadPolicies();
    } catch (error) {
      showFailure(error);
    }
  };

  const handlePublish = async (values, policyId) => {
    try {
      await updatePolicy({ ...values, status: Published }, policyId);

      Modal.success({
        title: getTranslatedString('landlordPortal.policies.successPublishedTitle'),
        content: getTranslatedString('landlordPortal.policies.successPublishedDesc', {
          policy_title: values.title,
        }),
        onOk: setMode(modes.LIST),
      });

      loadPolicies();
    } catch (error) {
      showFailure(error);
    }
  };

  const handleDeletePolicy = async (values, policyId) => {
    try {
      await deletePolicy(policyId);

      Modal.success({
        title: getTranslatedString('landlordPortal.policies.successDeletedTitle'),
        content: getTranslatedString('landlordPortal.policies.successDeletedDesc', {
          policy_title: values.title,
        }),
        onOk: setMode(modes.LIST),
      });

      loadPolicies();
    } catch (error) {
      showFailure(error);
    }
  };

  const handleArchivePolicy = async (values, policyId) => {
    try {
      await updatePolicy({ ...values, status: Archived }, policyId);

      Modal.success({
        title: getTranslatedString('landlordPortal.policies.successArchivedTitle'),
        content: getTranslatedString('landlordPortal.policies.successArchivedDesc', {
          policy_title: values.title,
        }),
        onOk: setMode(modes.LIST),
      });

      loadPolicies();
    } catch (error) {
      showFailure(error);
    }
  };

  const renderTable = () => {
    return (
      <Row>
        <Col span={24}>
          <Tabs defaultActiveKey="published_and_draft">
            <Tabs.TabPane tab="Published and draft" key="published_and_draft">
              <PoliciesTable
                onClickAddButton={setCreateModePolicy}
                onEditClick={(policyId) => editPolicy(policyId)}
                statusFilters={[Draft, Published].map((s) => ({ value: s, text: Statuses[s] }))}
                renderRowActions={(rowData) => (
                  <RowActions
                    rowData={rowData}
                    onDuplicate={(policyId) => duplicatePolicy(policyId)}
                    onArchive={handleArchivePolicy}
                  />
                )}
                onResetClick={() => loadPolicies()}
                policyIncluded={(p) => [Published, Draft].includes(p.status)}
                isMobile={isMobile}
              />
            </Tabs.TabPane>

            <Tabs.TabPane tab="Archived" key="archived">
              <PoliciesTable
                onClickAddButton={setCreateModePolicy}
                onEditClick={(policyId) => editPolicy(policyId)}
                statusFilters={[Archived].map((s) => ({ value: s, text: Statuses[s] }))}
                renderRowActions={(rowData) => (
                  <RowActions
                    rowData={rowData}
                    onDuplicate={(policyId) => duplicatePolicy(policyId)}
                    onDelete={handleDeletePolicy}
                  />
                )}
                onResetClick={() => loadPolicies()}
                policyIncluded={(p) => [Archived].includes(p.status)}
                isMobile={isMobile}
              />
            </Tabs.TabPane>
          </Tabs>
        </Col>
      </Row>
    );
  };

  const renderList = () => {
    return (
      <div style={{ padding: '24px 24px' }}>
        {<Typography.Title level={4}>Policies</Typography.Title>}
        {renderTable()}
      </div>
    );
  };

  const renderMode = () => {
    if (mode === modes.LIST) {
      return renderList();
    }
    if (mode === modes.CREATE) {
      return (
        <PolicyForm
          formTitle={getTranslatedString('landlordPortal.policies.create')}
          formName={CREATE_FORM}
          formData={currentPolicy}
          onClickBackButton={() => setMode(modes.LIST)}
          actionButtons={
            <ActionButtons
              status={currentPolicy.status}
              loading={loading}
              onCreate={handleCreate}
              onUpdate={handleUpdate}
              onArchive={handleArchivePolicy}
              onPublish={handlePublish}
              onDelete={handleDeletePolicy}
            />
          }
        />
      );
    }
    if (mode === modes.EDIT) {
      return (
        <PolicyForm
          formTitle={getTranslatedString('landlordPortal.policies.edit')}
          formName={EDIT_FORM}
          formData={currentPolicy}
          onClickBackButton={() => setMode(modes.LIST)}
          actionButtons={
            <ActionButtons
              status={currentPolicy.status}
              loading={loading}
              onCreate={handleCreate}
              onUpdate={handleUpdate}
              onArchive={handleArchivePolicy}
              onPublish={handlePublish}
              onDelete={handleDeletePolicy}
            />
          }
        />
      );
    }

    throw new Error(`Invalid mode: ${mode}`);
  };

  const contextData = {
    isMobile: isMobile,
    policies: policies,
    policyAssociations: policyAssociations,
    mode: mode,
    loading: loading,
    currentPolicy: currentPolicy,
  };

  // Write the contextData into the `Provider`,
  // making it available to descendant components
  return (
    <div className="body" role="main">
      <GreyContainer>
        <PoliciesContext.Provider value={contextData}>{renderMode()}</PoliciesContext.Provider>
      </GreyContainer>
    </div>
  );
};

export default connect(null, { showFailure })(LandlordPolicies);
